import { useEffect, useMemo, useState } from 'react'
import { Avatar, Button, DatePicker, Input, Modal, Radio, Spin, Tooltip } from 'antd'
import { DownOutlined, RightOutlined, SearchOutlined, UserAddOutlined } from '@ant-design/icons'
import { Dayjs } from 'dayjs'
import { useSchedule } from '../ScheduleContext'
import { useScheduledEventsByDateQuery } from '../scheduleQueries'
import { getInitials, UNASSIGNED_CREW_ID } from '../scheduleModel'
import { buildCrewLeadOptions, CrewLeadOption, CrewLeadSelection, filterCrewLeadOptions, formatEventTimeRange, getIneligibleReason, getWorkAreaCount } from './optimizeScheduleModel'
import './OptimizeScheduleModal.css'

type Props = {
  open: boolean
  initialDate: Dayjs
  onCancel: () => void
  onSubmit: (date: Dayjs, crewLeadId: CrewLeadSelection) => void
}

type CrewLeadAvatarProps = {
  option: CrewLeadOption
  size?: number
}

const CrewLeadAvatar = ({ option, size = 32 }: CrewLeadAvatarProps) => {
  if (option.id === UNASSIGNED_CREW_ID) {
    return (
      <Avatar size={size} icon={<UserAddOutlined />} className="crew-lead-avatar-unassigned" />
    )
  }

  const color = option.color || '#999'

  if (option.avatar && !option.hasStockAvatar) {
    return <Avatar size={size} src={option.avatar} style={{ border: `2px solid ${color}` }} />
  }

  return (
    <Avatar
      size={size}
      className="crew-lead-avatar-initials"
      style={{ backgroundColor: color, fontSize: Math.floor(size * 0.4) }}
    >
      {getInitials(option.name)}
    </Avatar>
  )
}

type CrewLeadRowProps = {
  option: CrewLeadOption
  isSelected: boolean
  isExpanded: boolean
  ineligibleReason: string | null
  onToggleExpanded: () => void
  onSelect: () => void
}

const CrewLeadRow = ({ option, isSelected, isExpanded, ineligibleReason, onToggleExpanded, onSelect }: CrewLeadRowProps) => {
  const plannedCount = option.plannedEvents.length
  const countLabel = `${plannedCount} Planned Event${plannedCount === 1 ? '' : 's'}`
  const isDisabled = ineligibleReason !== null

  return (
    <div className="crew-lead-row">
      <div
        className="crew-lead-row-header"
        onClick={() => {
          if (isDisabled || isSelected) {
            onToggleExpanded()
          } else {
            onSelect()
          }
        }}
      >
        <Radio
          checked={isSelected}
          disabled={isDisabled}
          onClick={(e) => e.stopPropagation()}
          onChange={onSelect}
        />
        <div className={isDisabled ? 'crew-lead-row-avatar-wrapper--disabled' : undefined}>
          <CrewLeadAvatar option={option} />
        </div>
        <div className="crew-lead-row-body">
          <div className={isDisabled ? 'crew-lead-row-name crew-lead-row-name--disabled' : 'crew-lead-row-name'}>
            {option.name}
          </div>
          {ineligibleReason && (
            <div className="crew-lead-row-reason">{ineligibleReason}</div>
          )}
        </div>
        <div className="crew-lead-row-count">{countLabel}</div>
        <span
          className="crew-lead-row-caret"
          onClick={(e) => {
            e.stopPropagation()
            onToggleExpanded()
          }}
        >
          {isExpanded ? <DownOutlined /> : <RightOutlined />}
        </span>
      </div>

      {isExpanded && plannedCount > 0 && (
        <div className="crew-lead-row-events">
          <div className="crew-lead-row-events-label">Planned Events</div>
          {option.plannedEvents.map((event) => {
            const workAreaCount = getWorkAreaCount(event)
            const crewBorderColor = option.color || '#d9d9d9'
            return (
              <div
                key={event.id}
                className="crew-lead-row-event-card"
                style={{ borderLeft: `3px solid ${crewBorderColor}` }}
              >
                <div className="crew-lead-row-event-details">
                  <div className="crew-lead-row-event-customer">
                    {event.customerDisplayName || 'Unknown Customer'}
                  </div>
                  <div className="crew-lead-row-event-job">{event.jobName}</div>
                  {workAreaCount > 0 && (
                    <div className="crew-lead-row-event-meta">
                      {workAreaCount} Work Area{workAreaCount === 1 ? '' : 's'}
                    </div>
                  )}
                </div>
                <div className="crew-lead-row-event-time">{formatEventTimeRange(event)}</div>
              </div>
            )
          })}
        </div>
      )}

      {isExpanded && plannedCount === 0 && (
        <div className="crew-lead-row-empty">No planned events for this day.</div>
      )}
    </div>
  )
}

const OptimizeScheduleModal = ({ open, initialDate, onCancel, onSubmit }: Props) => {
  const { crew, isCrewLoading } = useSchedule()
  const [selectedDate, setSelectedDate] = useState<Dayjs>(initialDate)
  const [search, setSearch] = useState('')
  const [selectedCrewLeadId, setSelectedCrewLeadId] = useState<CrewLeadSelection | null>(null)
  const [expandedCrewLeadId, setExpandedCrewLeadId] = useState<CrewLeadSelection | null>(null)

  useEffect(() => {
    if (open) {
      setSelectedDate(initialDate)
      setSearch('')
      setSelectedCrewLeadId(null)
      setExpandedCrewLeadId(null)
    }
  }, [open, initialDate])

  const dateString = selectedDate.format('YYYY-MM-DD')
  const scheduledEventsQuery = useScheduledEventsByDateQuery(open ? dateString : '')

  const events = scheduledEventsQuery.data?.scheduledEvents ?? []
  const visits = scheduledEventsQuery.data?.visits ?? []
  const calendarEventVisits = scheduledEventsQuery.data?.calendarEventVisits ?? []

  const crewLeadOptions = useMemo(() => {
    if (!crew) {
      return []
    }
    return buildCrewLeadOptions(crew, events, visits, calendarEventVisits, selectedDate)
  }, [crew, events, visits, calendarEventVisits, selectedDate])

  const filteredOptions = useMemo(() => filterCrewLeadOptions(crewLeadOptions, search), [crewLeadOptions, search])

  const selectedOption = crewLeadOptions.find((o) => o.id === selectedCrewLeadId) ?? null
  const selectedIneligibleReason = selectedOption ? getIneligibleReason(selectedOption) : null
  const canSubmit = selectedOption !== null && selectedIneligibleReason === null

  const submitTooltip = selectedOption === null
    ? 'Select a crew lead to optimize'
    : selectedIneligibleReason ?? ''

  const handleSubmit = () => {
    if (!canSubmit || selectedCrewLeadId === null) {
      return
    }
    onSubmit(selectedDate, selectedCrewLeadId)
  }

  const isLoading = isCrewLoading || scheduledEventsQuery.isLoading

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title="Optimize Schedule"
      width={560}
      footer={
        <div className="optimize-modal-footer">
          <Button onClick={onCancel}>Cancel</Button>
          <Tooltip title={submitTooltip}>
            <Button type="primary" onClick={handleSubmit} disabled={!canSubmit}>
              Optimize Schedule
            </Button>
          </Tooltip>
        </div>
      }
    >
      <div className="optimize-modal-description">
        Select a date and crew lead to <strong>optimize planned events.</strong> You can also include unassigned events.
      </div>

      <div className="optimize-modal-section">
        <div className="optimize-modal-label">Date</div>
        <DatePicker
          value={selectedDate}
          onChange={(d) => {
            if (d) {
              setSelectedDate(d)
              setSelectedCrewLeadId(null)
              setExpandedCrewLeadId(null)
            }
          }}
          allowClear={false}
          format="MMM D, YYYY"
          className="optimize-modal-date-picker"
        />
      </div>

      <div>
        <div className="optimize-modal-label">Crew Lead</div>
        <div className="optimize-modal-crew-list">
          <Input
            placeholder="Search Crew Leads"
            prefix={<SearchOutlined className="optimize-modal-search-icon" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="optimize-modal-search"
            allowClear
          />

          {isLoading ? (
            <div className="optimize-modal-loading">
              <Spin />
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="optimize-modal-empty">No crew leads found.</div>
          ) : (
            filteredOptions.map((option) => {
              const reason = getIneligibleReason(option)
              return (
                <CrewLeadRow
                  key={String(option.id)}
                  option={option}
                  isSelected={selectedCrewLeadId === option.id}
                  isExpanded={expandedCrewLeadId === option.id}
                  ineligibleReason={reason}
                  onToggleExpanded={() =>
                    setExpandedCrewLeadId((current) => (current === option.id ? null : option.id))
                  }
                  onSelect={() => {
                    setSelectedCrewLeadId(option.id)
                    setExpandedCrewLeadId(option.id)
                  }}
                />
              )
            })
          )}
        </div>
      </div>
    </Modal>
  )
}

export default OptimizeScheduleModal
